import { ICSVData, TObjectFilterValue } from 'common/types';
import {
    IUserProfileFragment,
    IFile,
    IUserExtraDateField,
    IUserExtraStringField,
    IUserExtraChoiceField,
    IUserExtraCheckboxField,
    IPortfolioItemGroupFragment,
    IPortfolioItemListItemOldFragment,
    IGroupListItemFragment,
    IUpdatePortfolioItemInput,
    ICreatePortfolioItemInput,
    IAppPermissionsFragment,
    ICreateGroupInput,
    ICreateUserInput,
    IRole,
    ITrainingListItemFragment,
    IUpdateGroupInput,
    IOfferGroupConditionMinMaxInput,
    IExtraCategoryOfferInput,
    ICurrentUserFragment,
    ISkillRequirementFragment,
    IUserListItemFragment,
} from 'graphql/types';
import { TExtraCategoriesFormInput } from 'organisation/types';

export interface IUserProfileSectionProps extends Omit<
    IUserProfileFragment | ICurrentUserFragment,
    'currentUserIsGroupManager'
> {
    withEditActions?: boolean;
    className?: string;
}

export type IUserPortfolioItemProps<T> = T & {
    className?: string;
    withEditActions?: boolean;
    onClick?(): void;
};

export interface IUserAvatar {
    profileImage: IFile;
    name: string;
}

export type TExtraUserField =
    | IUserExtraDateField
    | IUserExtraStringField
    | IUserExtraChoiceField
    | IUserExtraCheckboxField;

export enum IPortfolioItemType {
    Custom = 'CustomPortfolioItem',
    RelatedItem = 'RelatedPortfolioItem',
}

export type TPortfolioItemFormValues =
    | ICreatePortfolioItemInput
    | IUpdatePortfolioItemInput;

export interface IPortfolioGroup extends IPortfolioItemGroupFragment {
    portfolioItems: IPortfolioItemListItemOldFragment[];
}

export interface IExtraCategoryValueWithColor extends IExtraCategoryOfferInput {
    color?: string | null;
}

export interface IOldGroupConditionInput {
    id?: string;
    groups: TGroupListItem[];
    extraCategoryValues?: IExtraCategoryValueWithColor[];
    minMaxFilters?: IOfferGroupConditionMinMaxInput[];
}

export interface IGroupConditionInput {
    id?: string;
    groups: string[];
    extraCategoryValues?: IExtraCategoryValueWithColor[];
    minMaxFilters?: IOfferGroupConditionMinMaxInput[];
}

export type TPermissionValue = Record<string, boolean | undefined>;

export type TPermissionsKey = keyof Omit<
    Required<IAppPermissionsFragment>,
    '__typename'
>;
export type TPermissionTypeKey<T extends TPermissionsKey> = keyof Omit<
    NonNullable<IAppPermissionsFragment[T]>,
    '__typename'
>;

export type TAppPermissionsObj = {
    [K in TPermissionsKey]: {
        [P in TPermissionTypeKey<K>]: TPermissionValue;
    };
};

export interface ICreateUserFormValues extends ICreateUserInput {
    sendInvite?: boolean;
    sendTrainingInvite?: boolean;
}

export enum TFrontendRole {
    Author = IRole.Author,
    Manager = IRole.Manager,
    Owner = IRole.Owner,
    Planner = IRole.Planner,
    Trainer = IRole.Trainer,
    GroupManager = 'GROUP_MANAGER',
}

export interface IUserFilterValues {
    userGroups: TObjectFilterValue[];
    isCompliant?: boolean;
}

export type TGroupPermission = {
    canAddManagers?: boolean;
    canAddParticipants?: boolean;
};

type TGroupFormValuesBase = TExtraCategoriesFormInput & {
    skills?: ISkillRequirementFragment[];
    trainingIds?: ITrainingListItemFragment[];
    participantIds?: IUserListItemFragment[];
    managerIds?: IUserListItemFragment[];
};

export type TGroupCreateFormValues = TGroupFormValuesBase &
    Omit<
        ICreateGroupInput,
        'skills' | 'trainingIds' | 'participantIds' | 'managerIds'
    >;

export type TGroupUpdateFormValues = TGroupFormValuesBase &
    Omit<
        IUpdateGroupInput,
        'skills' | 'trainingIds' | 'participantIds' | 'managerIds'
    >;

export enum EGroupUserRole {
    Participant = 'Participant',
    Manager = 'Manager',
}

export enum EGroupFormTab {
    General = 0,
    Participants = 1,
    Skills = 2,
    Managers = 3,
}

export type TGroupListItem = Omit<IGroupListItemFragment, 'isEditable'>;

export enum EUsersImportType {
    Organisation = 'organisation',
    Group = 'group',
    Training = 'training',
    OfferEvent = 'offerEvent',
}

export interface IImportData {
    csvData: ICSVData;
    fileId: string;
}
